/**
 * @module services/bibliographyService
 * @description Local bibliography storage and sync service
 *
 * Stores Zotero collections locally as CSL-JSON for instant citation rendering.
 * Periodically syncs with Zotero API to detect changes.
 *
 * Architecture:
 * - On collection selection: fetch all items, convert to CSL-JSON, store locally
 * - Store Last-Modified-Version for efficient sync checks
 * - Every minute: check if library has been modified, update if needed
 */

import { db } from './indexedDBService';
import { proxyZoteroRequest } from './supabaseClient';
import { createLogger } from './loggerService';
import type { ZoteroItem, LibraryType } from './zoteroService';

const log = createLogger('bibliography');

// =============================================================================
// TYPES
// =============================================================================

/** Zotero item data (native format) - stored for direct API updates */
export interface ZoteroItemData {
  key?: string;
  version?: number;
  itemType: string;
  title?: string;
  creators?: Array<{
    creatorType: string;
    firstName?: string;
    lastName?: string;
    name?: string;
  }>;
  date?: string;
  dateModified?: string;
  dateAdded?: string;
  publicationTitle?: string;
  bookTitle?: string;
  publisher?: string;
  place?: string;
  volume?: string;
  issue?: string;
  pages?: string;
  edition?: string;
  series?: string;
  DOI?: string;
  ISBN?: string;
  ISSN?: string;
  url?: string;
  abstractNote?: string;
  language?: string;
  extra?: string;
  [key: string]: unknown;
}

/** CSL-JSON item format (generated on-the-fly from ZoteroItemData) */
export interface CSLItem {
  id: string;
  type: string;
  title?: string;
  'title-short'?: string;
  author?: CSLName[];
  editor?: CSLName[];
  translator?: CSLName[];
  'container-author'?: CSLName[];
  'collection-editor'?: CSLName[];
  composer?: CSLName[];
  director?: CSLName[];
  interviewer?: CSLName[];
  illustrator?: CSLName[];
  'original-author'?: CSLName[];
  recipient?: CSLName[];
  'reviewed-author'?: CSLName[];
  issued?: CSLDate;
  accessed?: CSLDate;
  'original-date'?: CSLDate;
  submitted?: CSLDate;
  'event-date'?: CSLDate;
  'container-title'?: string;
  'container-title-short'?: string;
  'collection-title'?: string;
  'collection-number'?: string;
  publisher?: string;
  'publisher-place'?: string;
  'original-publisher'?: string;
  'original-publisher-place'?: string;
  'original-title'?: string;
  page?: string;
  'number-of-pages'?: string;
  volume?: string;
  'number-of-volumes'?: string;
  issue?: string;
  edition?: string;
  section?: string;
  chapter?: string;
  ISBN?: string;
  ISSN?: string;
  DOI?: string;
  PMID?: string;
  PMCID?: string;
  URL?: string;
  abstract?: string;
  language?: string;
  source?: string;
  'call-number'?: string;
  archive?: string;
  'archive-place'?: string;
  'archive_location'?: string;
  'event-place'?: string;
  annote?: string;
  note?: string;
  keyword?: string;
  status?: string;
  genre?: string;
  medium?: string;
  dimensions?: string;
  scale?: string;
  version?: string;
  number?: string;
  references?: string;
  authority?: string;
  jurisdiction?: string;
  // Allow additional fields
  [key: string]: unknown;
}

/** CSL name format */
export interface CSLName {
  family?: string;
  given?: string;
  literal?: string;
  'non-dropping-particle'?: string;
  'dropping-particle'?: string;
  suffix?: string;
}

/** CSL date format */
export interface CSLDate {
  'date-parts'?: number[][];
  raw?: string;
  literal?: string;
}

/** Stored bibliography data - stores Zotero native format for lossless editing */
export interface StoredBibliography {
  projectId: string;
  libraryId: number;
  libraryType: LibraryType;
  collectionKey: string | null;
  collectionName: string;
  version: number;
  lastSync: string;
  /** Items stored in native Zotero format - CSL is generated on-the-fly when needed */
  items: Record<string, ZoteroItemData>;
}

/** Sync result */
export interface SyncResult {
  updated: boolean;
  itemsAdded: number;
  itemsUpdated: number;
  itemsRemoved: number;
  newVersion: number;
}

// =============================================================================
// ZOTERO TYPE TO CSL TYPE MAPPING
// =============================================================================

const ZOTERO_TO_CSL_TYPE: Record<string, string> = {
  'artwork': 'graphic',
  'audioRecording': 'song',
  'bill': 'bill',
  'blogPost': 'post-weblog',
  'book': 'book',
  'bookSection': 'chapter',
  'case': 'legal_case',
  'conferencePaper': 'paper-conference',
  'dataset': 'dataset',
  'dictionaryEntry': 'entry-dictionary',
  'document': 'document',
  'email': 'personal_communication',
  'encyclopediaArticle': 'entry-encyclopedia',
  'film': 'motion_picture',
  'forumPost': 'post',
  'hearing': 'hearing',
  'instantMessage': 'personal_communication',
  'interview': 'interview',
  'journalArticle': 'article-journal',
  'letter': 'personal_communication',
  'magazineArticle': 'article-magazine',
  'manuscript': 'manuscript',
  'map': 'map',
  'newspaperArticle': 'article-newspaper',
  'patent': 'patent',
  'podcast': 'broadcast',
  'presentation': 'speech',
  'radioBroadcast': 'broadcast',
  'report': 'report',
  'software': 'software',
  'standard': 'standard',
  'statute': 'legislation',
  'thesis': 'thesis',
  'tvBroadcast': 'broadcast',
  'videoRecording': 'motion_picture',
  'webpage': 'webpage',
};

// =============================================================================
// ZOTERO CREATOR TYPE TO CSL MAPPING
// =============================================================================

const ZOTERO_TO_CSL_CREATOR: Record<string, string> = {
  'author': 'author',
  'editor': 'editor',
  'seriesEditor': 'collection-editor',
  'translator': 'translator',
  'contributor': 'contributor',
  'reviewedAuthor': 'reviewed-author',
  'artist': 'author',
  'performer': 'author',
  'composer': 'composer',
  'wordsBy': 'author',
  'castMember': 'author',
  'director': 'director',
  'producer': 'author',
  'scriptwriter': 'author',
  'podcaster': 'author',
  'guest': 'author',
  'cartographer': 'author',
  'programmer': 'author',
  'presenter': 'author',
  'interviewee': 'author',
  'interviewer': 'interviewer',
  'recipient': 'recipient',
  'sponsor': 'author',
  'cosponsor': 'author',
  'counsel': 'author',
  'inventor': 'author',
  'attorneyAgent': 'author',
  'bookAuthor': 'container-author',
  'commenter': 'author',
  'illustrator': 'illustrator',
};

// =============================================================================
// CONVERSION HELPERS
// =============================================================================

/**
 * Parse date string to CSL date format
 */
function parseDate(dateStr: string | undefined): CSLDate | undefined {
  if (!dateStr) return undefined;

  // Handle date ranges (e.g., "2001-12-15/2001-12-31")
  if (dateStr.includes('/')) {
    const [start] = dateStr.split('/');
    return parseDate(start);
  }

  // Try to parse ISO date or year
  const match = dateStr.match(/^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?/);
  if (match) {
    const parts: number[] = [parseInt(match[1], 10)];
    if (match[2]) parts.push(parseInt(match[2], 10));
    if (match[3]) parts.push(parseInt(match[3], 10));
    return { 'date-parts': [parts] };
  }

  // Fallback to raw
  return { raw: dateStr };
}

/**
 * Parse CSL variables from Zotero's extra field
 */
function parseExtraField(extra: string | undefined): Record<string, string> {
  if (!extra) return {};

  const variables: Record<string, string> = {};
  const lines = extra.split('\n');

  for (const line of lines) {
    // Match "variable-name: value" pattern (case-insensitive key)
    const match = line.match(/^([a-zA-Z][a-zA-Z0-9-]*)\s*:\s*(.+)$/);
    if (match) {
      const [, key, value] = match;
      // Store with lowercase key for consistent lookup
      variables[key.toLowerCase()] = value.trim();
    }
  }

  return variables;
}

/**
 * Parse creator name from extra field format "Family || Given"
 */
function parseExtraCreator(value: string): CSLName {
  if (value.includes('||')) {
    const [family, given] = value.split('||').map(s => s.trim());
    return { family, given };
  }
  return { literal: value };
}

/**
 * Convert Zotero creators to CSL names grouped by role
 */
function convertCreators(
  creators: ZoteroItem['data']['creators'],
  extraVars: Record<string, string>
): Record<string, CSLName[]> {
  const result: Record<string, CSLName[]> = {};

  // Process Zotero creators
  if (creators) {
    for (const creator of creators) {
      const cslRole = ZOTERO_TO_CSL_CREATOR[creator.creatorType] || 'author';

      if (!result[cslRole]) {
        result[cslRole] = [];
      }

      if (creator.name) {
        result[cslRole].push({ literal: creator.name });
      } else {
        result[cslRole].push({
          family: creator.lastName || '',
          given: creator.firstName || '',
        });
      }
    }
  }

  // Process extra field creators
  const extraCreatorFields = [
    'director', 'editorial-director', 'illustrator', 'original-author',
    'container-author', 'collection-editor', 'interviewer', 'recipient'
  ];

  for (const field of extraCreatorFields) {
    if (extraVars[field]) {
      const cslField = field.replace(/-/g, '-');
      if (!result[cslField]) {
        result[cslField] = [];
      }
      result[cslField].push(parseExtraCreator(extraVars[field]));
    }
  }

  return result;
}

// =============================================================================
// MAIN CONVERSION FUNCTION
// =============================================================================

/**
 * Convert a Zotero item to CSL-JSON format
 * Maps ALL Zotero fields to their CSL equivalents
 */
export function zoteroItemToCSL(item: ZoteroItem): CSLItem {
  const data = item.data as Record<string, unknown>;
  const extra = parseExtraField(data.extra as string | undefined);

  // Override type from extra field if specified
  let cslType = ZOTERO_TO_CSL_TYPE[data.itemType as string] || 'document';
  if (extra.type) {
    cslType = extra.type.toLowerCase().replace(/ /g, '_');
  }

  const csl: CSLItem = {
    id: item.key,
    type: cslType,
  };

  // Title fields
  if (data.title) csl.title = data.title as string;
  if (data.shortTitle) csl['title-short'] = data.shortTitle as string;

  // Creators
  const creators = convertCreators(
    data.creators as ZoteroItem['data']['creators'],
    extra
  );
  for (const [role, names] of Object.entries(creators)) {
    if (names.length > 0) {
      (csl as Record<string, unknown>)[role] = names;
    }
  }

  // Dates
  if (data.date) csl.issued = parseDate(data.date as string);
  if (data.accessDate) csl.accessed = parseDate(data.accessDate as string);
  if (extra['original-date']) csl['original-date'] = parseDate(extra['original-date']);
  if (extra['submitted']) csl.submitted = parseDate(extra['submitted']);
  if (extra['event-date']) csl['event-date'] = parseDate(extra['event-date']);

  // Container titles (journals, books, etc.)
  if (data.publicationTitle) csl['container-title'] = data.publicationTitle as string;
  if (data.bookTitle) csl['container-title'] = data.bookTitle as string;
  if (data.encyclopediaTitle) csl['container-title'] = data.encyclopediaTitle as string;
  if (data.dictionaryTitle) csl['container-title'] = data.dictionaryTitle as string;
  if (data.proceedingsTitle) csl['container-title'] = data.proceedingsTitle as string;
  if (data.websiteTitle) csl['container-title'] = data.websiteTitle as string;
  if (data.blogTitle) csl['container-title'] = data.blogTitle as string;
  if (data.forumTitle) csl['container-title'] = data.forumTitle as string;
  if (data.programTitle) csl['container-title'] = data.programTitle as string;
  if (data.journalAbbreviation) csl['container-title-short'] = data.journalAbbreviation as string;

  // Series/Collection
  if (data.series) csl['collection-title'] = data.series as string;
  if (data.seriesTitle) csl['collection-title'] = data.seriesTitle as string;
  if (data.seriesNumber) csl['collection-number'] = data.seriesNumber as string;

  // Publisher info
  if (data.publisher) csl.publisher = data.publisher as string;
  if (data.place) csl['publisher-place'] = data.place as string;
  if (data.distributor) csl.publisher = data.distributor as string;
  if (data.label) csl.publisher = data.label as string;
  if (data.studio) csl.publisher = data.studio as string;
  if (data.network) csl.publisher = data.network as string;
  if (data.company) csl.publisher = data.company as string;
  if (data.institution) csl.publisher = data.institution as string;
  if (data.university) csl.publisher = data.university as string;

  // Original publication info (from extra)
  if (extra['original-publisher']) csl['original-publisher'] = extra['original-publisher'];
  if (extra['original-publisher-place']) csl['original-publisher-place'] = extra['original-publisher-place'];
  if (extra['original-title']) csl['original-title'] = extra['original-title'];

  // Pagination and volume
  if (data.pages) csl.page = data.pages as string;
  if (data.firstPage) csl.page = data.firstPage as string;
  if (data.numPages) csl['number-of-pages'] = data.numPages as string;
  if (data.volume) csl.volume = data.volume as string;
  if (data.numberOfVolumes) csl['number-of-volumes'] = data.numberOfVolumes as string;
  if (data.issue) csl.issue = data.issue as string;
  if (data.edition) csl.edition = data.edition as string;
  if (data.section) csl.section = data.section as string;
  if (extra['chapter-number']) csl.chapter = extra['chapter-number'];

  // Identifiers
  if (data.ISBN) csl.ISBN = data.ISBN as string;
  if (data.ISSN) csl.ISSN = data.ISSN as string;
  if (data.DOI) csl.DOI = data.DOI as string;
  if (extra.pmid) csl.PMID = extra.pmid;
  if (extra.pmcid) csl.PMCID = extra.pmcid;
  if (data.url) csl.URL = data.url as string;

  // Archive and access info
  if (data.archive) csl.archive = data.archive as string;
  if (data.archiveLocation) csl['archive_location'] = data.archiveLocation as string;
  if (extra['archive-place']) csl['archive-place'] = extra['archive-place'];
  if (data.libraryCatalog) csl.source = data.libraryCatalog as string;
  if (data.callNumber) csl['call-number'] = data.callNumber as string;

  // Event/Conference info
  if (data.conferenceName) csl['event-title'] = data.conferenceName as string;
  if (data.meetingName) csl['event-title'] = data.meetingName as string;
  if (extra['event-place']) csl['event-place'] = extra['event-place'];

  // Content and notes
  if (data.abstractNote) csl.abstract = data.abstractNote as string;
  if (data.language) csl.language = data.language as string;
  if (extra.status) csl.status = extra.status;

  // Media-specific fields
  if (data.audioRecordingFormat) csl.medium = data.audioRecordingFormat as string;
  if (data.videoRecordingFormat) csl.medium = data.videoRecordingFormat as string;
  if (data.artworkMedium) csl.medium = data.artworkMedium as string;
  if (data.interviewMedium) csl.medium = data.interviewMedium as string;
  if (data.letterType) csl.genre = data.letterType as string;
  if (data.manuscriptType) csl.genre = data.manuscriptType as string;
  if (data.mapType) csl.genre = data.mapType as string;
  if (data.presentationType) csl.genre = data.presentationType as string;
  if (data.reportType) csl.genre = data.reportType as string;
  if (data.thesisType) csl.genre = data.thesisType as string;
  if (data.websiteType) csl.genre = data.websiteType as string;
  if (data.postType) csl.genre = data.postType as string;
  if (data.genre) csl.genre = data.genre as string;

  // Artwork and maps
  if (data.artworkSize) csl.dimensions = data.artworkSize as string;
  if (data.scale) csl.scale = data.scale as string;

  // Software
  if (data.versionNumber) csl.version = data.versionNumber as string;
  if (data.system) csl.medium = data.system as string;

  // Recordings and broadcasts
  if (data.runningTime) csl.dimensions = data.runningTime as string;
  if (data.episodeNumber) csl.number = data.episodeNumber as string;

  // Legal fields
  if (data.court) csl.authority = data.court as string;
  if (data.issuingAuthority) csl.authority = data.issuingAuthority as string;
  if (data.legislativeBody) csl.authority = data.legislativeBody as string;
  if (data.country) csl.jurisdiction = data.country as string;
  if (data.session) csl['chapter-number'] = data.session as string;
  if (data.history) csl.references = data.history as string;
  if (data.reportNumber) csl.number = data.reportNumber as string;
  if (data.billNumber) csl.number = data.billNumber as string;
  if (data.codeNumber) csl.number = data.codeNumber as string;
  if (data.publicLawNumber) csl.number = data.publicLawNumber as string;
  if (data.patentNumber) csl.number = data.patentNumber as string;
  if (data.applicationNumber) csl.number = data.applicationNumber as string;
  if (data.docketNumber) csl.number = data.docketNumber as string;
  if (data.documentNumber) csl.number = data.documentNumber as string;
  if (data.priorityNumbers) csl.references = data.priorityNumbers as string;
  if (data.legalStatus) csl.status = data.legalStatus as string;
  if (data.code) csl['container-title'] = data.code as string;
  if (data.codeVolume) csl.volume = data.codeVolume as string;
  if (data.codePages) csl.page = data.codePages as string;
  if (data.reporter) csl['container-title'] = data.reporter as string;
  if (data.reporterVolume) csl.volume = data.reporterVolume as string;
  if (data.dateEnacted) csl.issued = parseDate(data.dateEnacted as string);
  if (data.dateDecided) csl.issued = parseDate(data.dateDecided as string);
  if (data.filingDate) csl.submitted = parseDate(data.filingDate as string);
  if (data.issueDate) csl.issued = parseDate(data.issueDate as string);
  if (data.nameOfAct) csl.title = data.nameOfAct as string;
  if (data.caseName) csl.title = data.caseName as string;
  if (data.subject) csl.title = data.subject as string;

  // Tags as keywords
  if (data.tags && Array.isArray(data.tags)) {
    const tags = data.tags as Array<{ tag: string }>;
    if (tags.length > 0) {
      csl.keyword = tags.map(t => t.tag).join(', ');
    }
  }

  return csl;
}

/**
 * Convert stored ZoteroItemData to CSL-JSON format
 * This is used for on-the-fly conversion when rendering citations
 */
export function zoteroDataToCSL(item: ZoteroItemData): CSLItem {
  const data = item as Record<string, unknown>;
  const extra = parseExtraField(data.extra as string | undefined);

  // Get the key from the item
  const itemKey = (item.key || data.key || '') as string;

  // Override type from extra field if specified
  let cslType = ZOTERO_TO_CSL_TYPE[item.itemType] || 'document';
  if (extra.type) {
    cslType = extra.type.toLowerCase().replace(/ /g, '_');
  }

  const csl: CSLItem = {
    id: itemKey,
    type: cslType,
  };

  // Title fields
  if (data.title) csl.title = data.title as string;
  if (data.shortTitle) csl['title-short'] = data.shortTitle as string;

  // Creators
  const creators = convertCreators(
    item.creators,
    extra
  );
  for (const [role, names] of Object.entries(creators)) {
    if (names.length > 0) {
      (csl as Record<string, unknown>)[role] = names;
    }
  }

  // Dates
  if (data.date) csl.issued = parseDate(data.date as string);
  if (data.accessDate) csl.accessed = parseDate(data.accessDate as string);
  if (extra['original-date']) csl['original-date'] = parseDate(extra['original-date']);
  if (extra['submitted']) csl.submitted = parseDate(extra['submitted']);
  if (extra['event-date']) csl['event-date'] = parseDate(extra['event-date']);

  // Container titles (journals, books, etc.)
  if (data.publicationTitle) csl['container-title'] = data.publicationTitle as string;
  if (data.bookTitle) csl['container-title'] = data.bookTitle as string;
  if (data.encyclopediaTitle) csl['container-title'] = data.encyclopediaTitle as string;
  if (data.dictionaryTitle) csl['container-title'] = data.dictionaryTitle as string;
  if (data.proceedingsTitle) csl['container-title'] = data.proceedingsTitle as string;
  if (data.websiteTitle) csl['container-title'] = data.websiteTitle as string;
  if (data.blogTitle) csl['container-title'] = data.blogTitle as string;
  if (data.forumTitle) csl['container-title'] = data.forumTitle as string;
  if (data.programTitle) csl['container-title'] = data.programTitle as string;
  if (data.journalAbbreviation) csl['container-title-short'] = data.journalAbbreviation as string;

  // Series/Collection
  if (data.series) csl['collection-title'] = data.series as string;
  if (data.seriesTitle) csl['collection-title'] = data.seriesTitle as string;
  if (data.seriesNumber) csl['collection-number'] = data.seriesNumber as string;

  // Publisher info
  if (data.publisher) csl.publisher = data.publisher as string;
  if (data.place) csl['publisher-place'] = data.place as string;
  if (data.distributor) csl.publisher = data.distributor as string;
  if (data.label) csl.publisher = data.label as string;
  if (data.studio) csl.publisher = data.studio as string;
  if (data.network) csl.publisher = data.network as string;
  if (data.company) csl.publisher = data.company as string;
  if (data.institution) csl.publisher = data.institution as string;
  if (data.university) csl.publisher = data.university as string;

  // Original publication info (from extra)
  if (extra['original-publisher']) csl['original-publisher'] = extra['original-publisher'];
  if (extra['original-publisher-place']) csl['original-publisher-place'] = extra['original-publisher-place'];
  if (extra['original-title']) csl['original-title'] = extra['original-title'];

  // Pagination and volume
  if (data.pages) csl.page = data.pages as string;
  if (data.firstPage) csl.page = data.firstPage as string;
  if (data.numPages) csl['number-of-pages'] = data.numPages as string;
  if (data.volume) csl.volume = data.volume as string;
  if (data.numberOfVolumes) csl['number-of-volumes'] = data.numberOfVolumes as string;
  if (data.issue) csl.issue = data.issue as string;
  if (data.edition) csl.edition = data.edition as string;
  if (data.section) csl.section = data.section as string;
  if (extra['chapter-number']) csl.chapter = extra['chapter-number'];

  // Identifiers
  if (data.ISBN) csl.ISBN = data.ISBN as string;
  if (data.ISSN) csl.ISSN = data.ISSN as string;
  if (data.DOI) csl.DOI = data.DOI as string;
  if (extra.pmid) csl.PMID = extra.pmid;
  if (extra.pmcid) csl.PMCID = extra.pmcid;
  if (data.url) csl.URL = data.url as string;

  // Archive and access info
  if (data.archive) csl.archive = data.archive as string;
  if (data.archiveLocation) csl['archive_location'] = data.archiveLocation as string;
  if (extra['archive-place']) csl['archive-place'] = extra['archive-place'];
  if (data.libraryCatalog) csl.source = data.libraryCatalog as string;
  if (data.callNumber) csl['call-number'] = data.callNumber as string;

  // Content and notes
  if (data.abstractNote) csl.abstract = data.abstractNote as string;
  if (data.language) csl.language = data.language as string;
  if (extra.status) csl.status = extra.status;

  // Media-specific fields
  if (data.genre) csl.genre = data.genre as string;

  // Tags as keywords
  if (data.tags && Array.isArray(data.tags)) {
    const tags = data.tags as Array<{ tag: string }>;
    if (tags.length > 0) {
      csl.keyword = tags.map(t => t.tag).join(', ');
    }
  }

  return csl;
}

// =============================================================================
// BIBLIOGRAPHY SERVICE CLASS
// =============================================================================

class BibliographyService {
  private syncIntervalId: ReturnType<typeof setInterval> | null = null;
  private readonly SYNC_INTERVAL_MS = 60000; // 1 minute
  private readonly STORAGE_KEY_PREFIX = 'bibliography_';

  /**
   * Get storage key for a project
   */
  private getStorageKey(projectId: string): string {
    return `${this.STORAGE_KEY_PREFIX}${projectId}`;
  }

  /**
   * Load stored bibliography for a project
   */
  async getBibliography(projectId: string): Promise<StoredBibliography | null> {
    try {
      const key = this.getStorageKey(projectId);
      const data = await db.settings.get<StoredBibliography | null>(key, null);
      return data;
    } catch (error) {
      log.error('Failed to load bibliography', { projectId, error });
      return null;
    }
  }

  /**
   * Save bibliography for a project
   */
  async saveBibliography(bibliography: StoredBibliography): Promise<void> {
    try {
      const key = this.getStorageKey(bibliography.projectId);
      await db.settings.set(key, bibliography);
      log.info('Bibliography saved', {
        projectId: bibliography.projectId,
        itemCount: Object.keys(bibliography.items).length,
        version: bibliography.version,
      });
    } catch (error) {
      log.error('Failed to save bibliography', { error });
      throw error;
    }
  }

  /**
   * Delete bibliography for a project
   */
  async deleteBibliography(projectId: string): Promise<void> {
    try {
      const key = this.getStorageKey(projectId);
      await db.settings.delete(key);
      log.info('Bibliography deleted', { projectId });
    } catch (error) {
      log.error('Failed to delete bibliography', { error });
    }
  }

  /**
   * Fetch and store a collection from Zotero
   */
  async loadCollection(
    projectId: string,
    libraryId: number,
    libraryType: LibraryType,
    collectionKey: string | null,
    collectionName: string
  ): Promise<StoredBibliography> {
    const endpoint = libraryType === 'user' ? `/users/${libraryId}` : `/groups/${libraryId}`;
    const itemsPath = collectionKey
      ? `${endpoint}/collections/${collectionKey}/items`
      : `${endpoint}/items`;

    log.info('Loading collection from Zotero', {
      libraryId,
      libraryType,
      collectionKey,
      collectionName,
    });

    try {
      // Fetch all items (paginated)
      const allItems: ZoteroItem[] = [];
      let start = 0;
      const limit = 100;
      let totalResults = 0;
      let lastVersion = 0;

      do {
        const response = await proxyZoteroRequest<ZoteroItem[]>({
          path: itemsPath,
          method: 'GET',
          params: {
            limit: String(limit),
            start: String(start),
            itemType: '-attachment || note',
            sort: 'creator',
            direction: 'asc',
          },
        });

        if (response.data) {
          allItems.push(...response.data);
        }

        totalResults = response.headers.totalResults || allItems.length;
        lastVersion = response.headers.lastModifiedVersion || 0;
        start += limit;

      } while (start < totalResults);

      log.info('Fetched items from Zotero', {
        count: allItems.length,
        version: lastVersion,
      });

      // Store in native Zotero format (CSL is generated on-the-fly when needed)
      const items: Record<string, ZoteroItemData> = {};
      for (const item of allItems) {
        items[item.key] = {
          ...item.data,
          key: item.key,
          version: item.version,
        } as ZoteroItemData;
      }

      // Create bibliography record
      const bibliography: StoredBibliography = {
        projectId,
        libraryId,
        libraryType,
        collectionKey,
        collectionName,
        version: lastVersion,
        lastSync: new Date().toISOString(),
        items,
      };

      // Save to IndexedDB
      await this.saveBibliography(bibliography);

      return bibliography;
    } catch (error) {
      log.error('Failed to load collection', { error });
      throw error;
    }
  }

  /**
   * Sync bibliography with Zotero (check for updates)
   */
  async syncBibliography(projectId: string): Promise<SyncResult | null> {
    const bibliography = await this.getBibliography(projectId);
    if (!bibliography) {
      log.debug('No bibliography to sync', { projectId });
      return null;
    }

    const { libraryId, libraryType, collectionKey, version } = bibliography;
    const endpoint = libraryType === 'user' ? `/users/${libraryId}` : `/groups/${libraryId}`;
    const versionsPath = collectionKey
      ? `${endpoint}/collections/${collectionKey}/items`
      : `${endpoint}/items`;

    try {
      // Check for updates using ?since=<version>&format=versions
      const response = await proxyZoteroRequest<Record<string, number>>({
        path: versionsPath,
        method: 'GET',
        params: {
          since: String(version),
          format: 'versions',
          itemType: '-attachment || note',
        },
      });

      const newVersion = response.headers.lastModifiedVersion || version;

      // No changes
      if (!response.data || Object.keys(response.data).length === 0) {
        log.debug('Bibliography up to date', { projectId, version });
        return {
          updated: false,
          itemsAdded: 0,
          itemsUpdated: 0,
          itemsRemoved: 0,
          newVersion,
        };
      }

      // There are changes - fetch updated items
      const changedKeys = Object.keys(response.data);
      log.info('Bibliography has updates', {
        projectId,
        changedItems: changedKeys.length,
        oldVersion: version,
        newVersion,
      });

      // Fetch changed items in batches of 50
      const updatedItems: ZoteroItem[] = [];
      for (let i = 0; i < changedKeys.length; i += 50) {
        const batchKeys = changedKeys.slice(i, i + 50);
        const itemsResponse = await proxyZoteroRequest<ZoteroItem[]>({
          path: `${endpoint}/items`,
          method: 'GET',
          params: {
            itemKey: batchKeys.join(','),
          },
        });
        if (itemsResponse.data) {
          updatedItems.push(...itemsResponse.data);
        }
      }

      // Check for deleted items
      const deletedResponse = await proxyZoteroRequest<{ items: string[] }>({
        path: `${endpoint}/deleted`,
        method: 'GET',
        params: {
          since: String(version),
        },
      });

      const deletedKeys = deletedResponse.data?.items || [];

      // Update local bibliography with native Zotero format
      let itemsAdded = 0;
      let itemsUpdated = 0;
      let itemsRemoved = 0;

      for (const item of updatedItems) {
        if (bibliography.items[item.key]) {
          itemsUpdated++;
        } else {
          itemsAdded++;
        }
        // Store in native Zotero format
        bibliography.items[item.key] = {
          ...item.data,
          key: item.key,
          version: item.version,
        } as ZoteroItemData;
      }

      for (const key of deletedKeys) {
        if (bibliography.items[key]) {
          delete bibliography.items[key];
          itemsRemoved++;
        }
      }

      // Update version and save
      bibliography.version = newVersion;
      bibliography.lastSync = new Date().toISOString();
      await this.saveBibliography(bibliography);

      log.info('Bibliography synced', {
        projectId,
        itemsAdded,
        itemsUpdated,
        itemsRemoved,
        newVersion,
      });

      return {
        updated: true,
        itemsAdded,
        itemsUpdated,
        itemsRemoved,
        newVersion,
      };
    } catch (error) {
      log.error('Failed to sync bibliography', { projectId, error });
      return null;
    }
  }

  /**
   * Get a single item from the bibliography (native Zotero format)
   */
  async getItem(projectId: string, itemKey: string): Promise<ZoteroItemData | null> {
    const bibliography = await this.getBibliography(projectId);
    if (!bibliography) return null;
    return bibliography.items[itemKey] || null;
  }

  /**
   * Get a single item as CSL (converted on-the-fly)
   */
  async getItemAsCSL(projectId: string, itemKey: string): Promise<CSLItem | null> {
    const item = await this.getItem(projectId, itemKey);
    if (!item) return null;
    return zoteroDataToCSL(item);
  }

  /**
   * Get all items from the bibliography (native Zotero format)
   */
  async getAllItems(projectId: string): Promise<ZoteroItemData[]> {
    const bibliography = await this.getBibliography(projectId);
    if (!bibliography) return [];
    return Object.values(bibliography.items);
  }

  /**
   * Get all items as CSL (converted on-the-fly)
   */
  async getAllItemsAsCSL(projectId: string): Promise<CSLItem[]> {
    const items = await this.getAllItems(projectId);
    return items.map(item => zoteroDataToCSL(item));
  }

  /**
   * Start periodic sync for a project
   */
  startPeriodicSync(projectId: string, onUpdate?: (result: SyncResult) => void): void {
    this.stopPeriodicSync();

    log.info('Starting periodic bibliography sync', {
      projectId,
      intervalMs: this.SYNC_INTERVAL_MS,
    });

    this.syncIntervalId = setInterval(async () => {
      const result = await this.syncBibliography(projectId);
      if (result && result.updated && onUpdate) {
        onUpdate(result);
      }
    }, this.SYNC_INTERVAL_MS);
  }

  /**
   * Stop periodic sync
   */
  stopPeriodicSync(): void {
    if (this.syncIntervalId) {
      clearInterval(this.syncIntervalId);
      this.syncIntervalId = null;
      log.info('Stopped periodic bibliography sync');
    }
  }

  /**
   * Check if a bibliography exists for a project
   */
  async hasBibliography(projectId: string): Promise<boolean> {
    const bibliography = await this.getBibliography(projectId);
    return bibliography !== null;
  }

  /**
   * Get bibliography metadata (without items)
   */
  async getBibliographyInfo(projectId: string): Promise<{
    collectionName: string;
    itemCount: number;
    lastSync: string;
    version: number;
  } | null> {
    const bibliography = await this.getBibliography(projectId);
    if (!bibliography) return null;

    return {
      collectionName: bibliography.collectionName,
      itemCount: Object.keys(bibliography.items).length,
      lastSync: bibliography.lastSync,
      version: bibliography.version,
    };
  }

  /**
   * Update a single item in the bibliography and push to Zotero
   * Accepts native Zotero format - no conversion needed
   */
  async updateItemAndPushToZotero(
    projectId: string,
    itemKey: string,
    updatedItem: ZoteroItemData
  ): Promise<{ localSaved: boolean; zoteroSynced: boolean; error?: string }> {
    const result = { localSaved: false, zoteroSynced: false, error: undefined as string | undefined };

    try {
      // Get or create bibliography
      let bibliography = await this.getBibliography(projectId);
      if (!bibliography) {
        log.warn('No bibliography for project, cannot update item', { projectId, itemKey });
        result.error = 'No bibliography exists for this project';
        return result;
      }

      // Update local item with modification timestamp
      updatedItem._localModified = new Date().toISOString();
      bibliography.items[itemKey] = updatedItem;

      // Save to IndexedDB
      await this.saveBibliography(bibliography);
      result.localSaved = true;
      log.info('Item updated locally', { projectId, itemKey, localModified: updatedItem._localModified });

      // Push to Zotero if we have library info
      if (bibliography.libraryId > 0) {
        try {
          const { updateItemDirect } = await import('./zoteroService');

          log.debug('Pushing item to Zotero', { libraryId: bibliography.libraryId, itemKey });

          const syncResult = await updateItemDirect(
            bibliography.libraryId,
            bibliography.libraryType,
            itemKey,
            updatedItem
          );

          if (syncResult.success) {
            result.zoteroSynced = true;
            // Update local version after successful push
            updatedItem.version = syncResult.data.version;
            delete updatedItem._localModified;
            await this.saveBibliography(bibliography);
            log.info('Item pushed to Zotero successfully', { itemKey, version: syncResult.data.version });
          } else {
            const failedResult = syncResult as { success: false; error: string };
            result.error = failedResult.error;
            log.warn('Failed to push item to Zotero', { itemKey, error: failedResult.error });
          }
        } catch (zoteroError) {
          result.error = zoteroError instanceof Error ? zoteroError.message : 'Zotero sync failed';
          log.error('Error pushing to Zotero', { itemKey, error: result.error });
        }
      } else {
        log.debug('Skipping Zotero push - no library ID', { projectId, libraryId: bibliography.libraryId });
      }

      return result;
    } catch (error) {
      result.error = error instanceof Error ? error.message : 'Failed to update item';
      log.error('Failed to update item', { projectId, itemKey, error: result.error });
      return result;
    }
  }

  /**
   * Bidirectional sync: compare local vs Zotero timestamps and sync in both directions
   * - Items modified locally (with _localModified) are pushed to Zotero if newer
   * - Items modified on Zotero are pulled to local if newer
   */
  async bidirectionalSync(projectId: string): Promise<{
    pushed: number;
    pulled: number;
    errors: string[];
  }> {
    const result = { pushed: 0, pulled: 0, errors: [] as string[] };

    const bibliography = await this.getBibliography(projectId);
    if (!bibliography) {
      log.debug('No bibliography to sync', { projectId });
      return result;
    }

    if (bibliography.libraryId === 0) {
      log.debug('No Zotero library linked, skipping bidirectional sync', { projectId });
      return result;
    }

    const { libraryId, libraryType } = bibliography;
    const itemKeys = Object.keys(bibliography.items);

    if (itemKeys.length === 0) {
      log.debug('No items in bibliography', { projectId });
      return result;
    }

    log.info('Starting bidirectional sync', { projectId, itemCount: itemKeys.length });

    try {
      // Import zoteroService dynamically
      const { getItemsByKeys, updateItemDirect } = await import('./zoteroService');

      // Fetch current state of all items from Zotero
      const zoteroResult = await getItemsByKeys(libraryId, libraryType, itemKeys);
      if (!zoteroResult.success) {
        const failedResult = zoteroResult as { success: false; error: string };
        result.errors.push(`Failed to fetch Zotero items: ${failedResult.error}`);
        return result;
      }

      const zoteroItems = zoteroResult.data;
      const zoteroItemMap = new Map(zoteroItems.map(item => [item.key, item]));

      // Compare each local item with Zotero
      for (const [itemKey, localItem] of Object.entries(bibliography.items)) {
        const zoteroItem = zoteroItemMap.get(itemKey);

        if (!zoteroItem) {
          // Item doesn't exist on Zotero - might have been deleted or never existed there
          log.debug('Item not found on Zotero', { itemKey });
          continue;
        }

        const localModified = localItem._localModified as string | undefined;
        const zoteroModified = zoteroItem.data.dateModified;

        // Skip if no modifications to compare
        if (!localModified && !zoteroModified) {
          continue;
        }

        const localDate = localModified ? new Date(localModified) : new Date(0);
        const zoteroDate = zoteroModified ? new Date(zoteroModified) : new Date(0);

        log.debug('Comparing timestamps', {
          itemKey,
          localModified: localDate.toISOString(),
          zoteroModified: zoteroDate.toISOString(),
          localIsNewer: localDate > zoteroDate,
        });

        if (localModified && localDate > zoteroDate) {
          // Local is newer - push to Zotero
          log.info('Pushing local changes to Zotero', { itemKey });
          try {
            const pushResult = await updateItemDirect(libraryId, libraryType, itemKey, localItem);
            if (pushResult.success) {
              // Clear the local modified flag and update version after successful push
              delete localItem._localModified;
              localItem.version = pushResult.data.version;
              result.pushed++;
              log.info('Item pushed to Zotero', { itemKey });
            } else {
              const failedPush = pushResult as { success: false; error: string };
              result.errors.push(`Failed to push ${itemKey}: ${failedPush.error}`);
            }
          } catch (pushError) {
            result.errors.push(`Error pushing ${itemKey}: ${pushError instanceof Error ? pushError.message : 'Unknown error'}`);
          }
        } else if (zoteroModified && zoteroDate > localDate) {
          // Zotero is newer - pull to local (store native Zotero format)
          log.info('Pulling Zotero changes to local', { itemKey });
          bibliography.items[itemKey] = {
            ...zoteroItem.data,
            key: zoteroItem.key,
            version: zoteroItem.version,
          } as ZoteroItemData;
          result.pulled++;
        }
      }

      // Save updated bibliography if any changes
      if (result.pushed > 0 || result.pulled > 0) {
        bibliography.lastSync = new Date().toISOString();
        await this.saveBibliography(bibliography);
        log.info('Bidirectional sync complete', result);
      }

      return result;
    } catch (error) {
      result.errors.push(`Sync error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      log.error('Bidirectional sync failed', { projectId, error });
      return result;
    }
  }
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

export const bibliographyService = new BibliographyService();

export default bibliographyService;
